import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Car, Coffee, MapPin, Users, Wifi, Clock, Heart, Award, CheckCircle, ChevronRight, ChevronDown } from 'lucide-react';
import { SiteHeader, SiteFooter, MobileBookBar } from '../components/SiteLayout';
import BookingDatePicker from '../components/BookingDatePicker';
import { trackView, trackBookingClick } from '../components/lpTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const DIRECT_BOOKING_URL = process.env.REACT_APP_BOOKING_URL;

const ICONS = { car: Car, coffee: Coffee, mappin: MapPin, users: Users, wifi: Wifi, clock: Clock, heart: Heart, award: Award };

function buildBookingUrl(checkIn, checkOut, promo) {
  const params = new URLSearchParams();
  params.set('locale', 'en');
  params.set('currency', 'GBP');
  params.set('trackPage', 'no');
  if (checkIn) params.set('checkInDate', checkIn);
  if (checkOut) params.set('checkOutDate', checkOut);
  if (promo) params.set('promoCode', promo);
  return `${DIRECT_BOOKING_URL}?${params.toString()}`;
}

function MinimalHeader({ campaignName, onBook }) {
  const bookingUrl = buildBookingUrl();
  return (
    <header className="fixed top-0 w-full z-50 bg-[#0F1419]/95 backdrop-blur-xl border-b border-[#C9A961]/20" data-testid="lp-minimal-header">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-3">
          <div className="border-2 border-[#C9A961] p-2 sm:p-3 bg-[#0F1419]">
            <div className="text-xl sm:text-2xl font-serif text-[#C9A961] leading-none tracking-tighter">W</div>
          </div>
          <div>
            <div className="text-base sm:text-xl font-serif text-[#C9A961] tracking-tight leading-tight">Wentworth House</div>
            <div className="text-[8px] sm:text-[9px] tracking-[0.3em] text-[#C9A961]/60 uppercase">Bath · Est. 2003</div>
          </div>
        </Link>
        <a href={bookingUrl} target="_blank" rel="noopener noreferrer external" onClick={onBook}
          className="bg-[#C9A961] text-[#0F1419] px-4 sm:px-8 py-3 text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#D4B76E] transition-all duration-300"
          data-testid="lp-header-book-btn">
          Book Now
        </a>
      </nav>
    </header>
  );
}

function MinimalFooter() {
  return (
    <footer className="bg-[#0a0d12] border-t border-[#C9A961]/15 py-8 px-4 sm:px-6 text-center" data-testid="lp-minimal-footer">
      <div className="max-w-4xl mx-auto space-y-3">
        <div className="flex justify-center gap-6 text-xs text-[#E8E4DC]/40">
          <Link to="/privacy-policy" className="hover:text-[#C9A961]">Privacy</Link>
          <Link to="/terms-of-service" className="hover:text-[#C9A961]">Terms</Link>
          <Link to="/cookie-policy" className="hover:text-[#C9A961]">Cookies</Link>
          <Link to="/" className="hover:text-[#C9A961]">Main Site</Link>
        </div>
        <p className="text-xs text-[#E8E4DC]/30">© {new Date().getFullYear()} Wentworth House Hotel · 106 Bloomfield Road, Bath BA2 2AP · 01225 339193</p>
      </div>
    </footer>
  );
}

function Benefit({ icon, title, desc }) {
  const Icon = ICONS[icon] || CheckCircle;
  return (
    <div className="bg-[#1a1f2e]/60 border border-[#C9A961]/15 p-6 hover:border-[#C9A961]/40 transition-colors" data-testid="lp-benefit">
      <Icon className="w-6 h-6 text-[#C9A961] mb-4" strokeWidth={1.5} />
      <h3 className="text-base text-[#E8E4DC] font-serif mb-2">{title}</h3>
      <p className="text-sm text-[#E8E4DC]/60 leading-relaxed">{desc}</p>
    </div>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-[#C9A961]/15 py-5">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left gap-4"
        data-testid={`lp-faq-item-${idx}`}>
        <span className="text-base text-[#E8E4DC] font-serif">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#C9A961] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="mt-3 text-sm text-[#E8E4DC]/70 leading-relaxed" data-testid={`lp-faq-answer-${idx}`}>{a}</p>}
    </div>
  );
}

export default function LandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const trackedRef = React.useRef(false);

  useEffect(() => {
    axios.get(`${API_URL}/content/landing-page/${slug}`)
      .then(r => {
        if (r.data.expired) {
          setExpired(true);
          setTimeout(() => navigate('/', { replace: true }), 3000);
        } else {
          setPage(r.data);
          if (!trackedRef.current) {
            trackedRef.current = true;
            trackView(r.data);
          }
        }
      })
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
        <div className="text-[#C9A961] font-serif text-lg">Loading...</div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-4">This offer has ended</h1>
          <p className="text-sm text-[#E8E4DC]/60 mb-6">Redirecting you to our homepage for the latest availability…</p>
          <Link to="/" className="inline-block bg-[#C9A961] text-[#0F1419] px-8 py-3 text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#D4B76E]">Go Home</Link>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-4">Page not found</h1>
          <Link to="/" className="inline-block bg-[#C9A961] text-[#0F1419] px-8 py-3 text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#D4B76E]">Go Home</Link>
        </div>
      </div>
    );
  }

  const fullSite = page.layout_type === 'full_site';
  const heroImg = page.hero_image || `${API_URL}/uploads/d6eb7e015bf94972.jpg`;
  const heroBookingUrl = buildBookingUrl(null, null, page.promo_code);
  const onBook = () => trackBookingClick(page);

  return (
    <div className="min-h-screen bg-[#0F1419] text-[#E8E4DC]" data-testid="lp-root">
      <Helmet>
        <title>{page.seo_title || `${page.campaign_name} | Wentworth House Hotel`}</title>
        <meta name="description" content={page.seo_description || page.headline || ''} />
        {page.noindex && <meta name="robots" content="noindex,nofollow" />}
        <link rel="canonical" href={`https://www.wentworthhouse.co.uk/lp/${page.slug}`} />
      </Helmet>

      {fullSite ? <SiteHeader /> : <MinimalHeader campaignName={page.campaign_name} onBook={onBook} />}

      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-10 overflow-hidden" data-testid="lp-hero">
        <div className="absolute inset-0 z-0">
          <img src={heroImg} alt={page.campaign_name} className="w-full h-full object-cover opacity-30" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1419]/60 via-[#0F1419]/80 to-[#0F1419]"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          {page.promo_code && (
            <div className="inline-block mb-6 px-4 py-1.5 border border-[#C9A961]/40 bg-[#C9A961]/10 text-[#C9A961] text-xs tracking-[0.2em] uppercase" data-testid="lp-promo-code">
              Promo Code: <span className="font-bold">{page.promo_code}</span>
            </div>
          )}
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif text-[#E8E4DC] leading-tight mb-6" data-testid="lp-headline">
            {page.headline}
          </h1>
          {page.subheadline && (
            <p className="text-base sm:text-lg text-[#E8E4DC]/70 max-w-2xl mx-auto mb-10" data-testid="lp-subheadline">
              {page.subheadline}
            </p>
          )}
          <div className="max-w-4xl mx-auto">
            <BookingDatePicker promoCode={page.promo_code} onBookClick={onBook} />
          </div>
          <div className="mt-6">
            <a href={heroBookingUrl} target="_blank" rel="noopener noreferrer external" onClick={onBook}
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#C9A961] hover:text-[#D4B76E] border-b border-[#C9A961]/30 hover:border-[#C9A961] pb-1 transition-all"
              data-testid="lp-hero-direct-link">
              Or open booking in new tab <ChevronRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* Trust signals bar */}
      {page.trust_signals && page.trust_signals.length > 0 && (
        <section className="bg-[#1a1f2e] border-y border-[#C9A961]/15 py-5 px-4 sm:px-6" data-testid="lp-trust-bar">
          <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-x-10 gap-y-3">
            {page.trust_signals.map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-[#E8E4DC]/80">
                <CheckCircle className="w-4 h-4 text-[#C9A961] shrink-0" strokeWidth={2} />
                <span>{t.text}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Benefits */}
      {page.benefits && page.benefits.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-10" data-testid="lp-benefits-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961] mb-3">Why Book Direct</div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC]">Why Guests Choose Wentworth House</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {page.benefits.map((b, i) => <Benefit key={i} {...b} />)}
            </div>
          </div>
        </section>
      )}

      {/* Offer section */}
      {page.offer_section && (page.offer_section.heading || page.offer_section.text) && (
        <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10 bg-gradient-to-b from-[#0F1419] to-[#1a1f2e]" data-testid="lp-offer-section">
          <div className="max-w-4xl mx-auto text-center">
            {page.offer_section.highlight && (
              <div className="inline-block mb-5 px-4 py-1.5 border border-[#C9A961] text-[#C9A961] text-xs tracking-[0.2em] uppercase">
                {page.offer_section.highlight}
              </div>
            )}
            <h2 className="text-2xl sm:text-4xl font-serif text-[#E8E4DC] mb-5">{page.offer_section.heading}</h2>
            <p className="text-base sm:text-lg text-[#E8E4DC]/70 leading-relaxed mb-10">{page.offer_section.text}</p>
            <a href={heroBookingUrl} target="_blank" rel="noopener noreferrer external" onClick={onBook}
              className="inline-flex items-center gap-2 bg-[#C9A961] text-[#0F1419] px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#D4B76E] transition-all"
              data-testid="lp-offer-cta">
              {page.cta_text || 'Check Availability'} <ChevronRight className="w-4 h-4" strokeWidth={3} />
            </a>
          </div>
        </section>
      )}

      {/* Room recommendation */}
      {page.room_recommendation && (page.room_recommendation.heading || page.room_recommendation.text) && (
        <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10" data-testid="lp-room-rec">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961] mb-3">Recommended Room</div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-4">{page.room_recommendation.heading}</h2>
              <p className="text-sm sm:text-base text-[#E8E4DC]/70 leading-relaxed mb-6">{page.room_recommendation.text}</p>
              <Link to="/rooms" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#C9A961] hover:text-[#D4B76E] border-b border-[#C9A961]/30 hover:border-[#C9A961] pb-1 transition-all">
                See all rooms <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden border border-[#C9A961]/20">
              <img src={`${API_URL}/uploads/f4831d291ee147fe.jpg`} alt={page.room_recommendation.heading} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </section>
      )}

      {/* Local context */}
      {page.local_context && (
        <section className="py-14 sm:py-16 px-4 sm:px-6 lg:px-10 bg-[#1a1f2e]/50" data-testid="lp-local">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961] mb-3">Bath, UK</div>
            <p className="text-base sm:text-lg text-[#E8E4DC]/80 leading-relaxed font-light">{page.local_context}</p>
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faq && page.faq.length > 0 && (
        <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10" data-testid="lp-faq-section">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <div className="text-xs tracking-[0.3em] uppercase text-[#C9A961] mb-3">Questions</div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC]">Frequently Asked</h2>
            </div>
            <div>{page.faq.map((f, i) => <FAQItem key={i} idx={i} {...f} />)}</div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      {page.final_cta && (page.final_cta.heading || page.final_cta.text) && (
        <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-10 bg-gradient-to-b from-[#0F1419] to-[#1a1f2e] text-center" data-testid="lp-final-cta">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-4xl font-serif text-[#E8E4DC] mb-4">{page.final_cta.heading}</h2>
            {page.final_cta.text && <p className="text-sm sm:text-base text-[#E8E4DC]/70 mb-8">{page.final_cta.text}</p>}
            <div className="max-w-xl mx-auto">
              <BookingDatePicker promoCode={page.promo_code} onBookClick={onBook} />
            </div>
          </div>
        </section>
      )}

      {fullSite ? <SiteFooter /> : <MinimalFooter />}
      <MobileBookBar />
    </div>
  );
}
